/**
 * A vertical list of links with icons
 */

import { FC } from 'react';
import { twJoin } from 'tailwind-merge';

import { DbtResourceIcon, ResourceTypeExplorer, TestStatus } from '@dbt-labs/dbt-dag';
import { Icon, Link, RyeconEyeClosed } from '@dbt-labs/sourdough';

import { Badge } from './Badge';
import { TestStatusIcon } from './TestStatusIcon';

type ResourceIcon =
  | {
      resourceType: ResourceTypeExplorer;
      status?: unknown;
    }
  | {
      resourceType: 'test';
      status: TestStatus | null | undefined;
    };

const getTestStatus = (icon: ResourceIcon) => {
  if (icon.resourceType === 'test') {
    return icon.status as TestStatus | null | undefined;
  }
  return null;
};

export type IconLink = ResourceIcon & {
  name: string;
  href: string | null;
  isInternal: boolean;
};

export type PublicResourceIconLink = IconLink & {
  isReferenced: boolean;
};

interface LinkListProps {
  links: IconLink[] | undefined | null;
  alternateColors?: boolean;
}

export const ResourceLinkList: FC<LinkListProps> = ({ links, alternateColors }) => {
  if (links == null) {
    return null;
  }
  if (!links.length) {
    return (
      <div data-testid="resourceListMessage" className="mt-2 italic text-fgDecorative">
        No relationships found.
      </div>
    );
  }

  return (
    <div data-testid="resourceListLinks" className="space-y-2">
      {links.map(({ href, name, isInternal, resourceType, status }, index) => {
        const testStatus = getTestStatus({ resourceType, status });
        return (
          <div
            data-testid="resourceListLink"
            key={`${href}|${name}`}
            className={twJoin(
              'flex',
              alternateColors && index % 2 === 1 && 'bg-bgBackground',
            )}
          >
            {href && (
              <Link isInternal={isInternal} to={href || '#'} className={'flex-0 flex'}>
                <span className="flex-0 m-1 h-4 w-4">
                  <span className="mx-auto">
                    {testStatus && (
                      <TestStatusIcon className="align-top" status={testStatus} />
                    )}
                    {!status && (
                      <DbtResourceIcon
                        resource={resourceType}
                        className="align-top text-current"
                      />
                    )}
                  </span>
                </span>
                <span className="align-middle">{name}</span>
              </Link>
            )}
            {!href && resourceType === 'project' && (
              <div className="flex">
                <span className="flex-0 leading-6 text-fgDecorative">{name}</span>
                <Badge className="flex-0 ml-2 align-middle">
                  <Icon size="xs" className="mr-1 mt-0.5" ryecon={RyeconEyeClosed} />
                  <span className="text-xs leading-5">Private</span>
                </Badge>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
